import { useEffect, useMemo, useState, useCallback } from 'react';
import { useDateFormat, formatDateWithFmt } from './DateFormatContext';
import { Link } from 'react-router-dom';
import Icon from './Icons';
import { usePullToRefresh } from './hooks';
import { SkeletonActivityFeed } from './Skeleton';
import ErrorScreen from './ErrorScreen';
import { photoUrl } from './photoUtils';

function parseDate(value) {
  if (!value) return null;
  const normalized = value.includes('T') ? value : value.replace(' ', 'T');
  return new Date(normalized.length === 10 ? `${normalized}T00:00:00` : normalized);
}


function formatLoggedTime(value) {
  if (!value) return null;
  const date = new Date(value.includes('T') ? value : `${value.replace(' ', 'T')}Z`);
  if (!date || Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatGroupDate(value, fmt) {
  const date = parseDate(value);
  if (!date || Number.isNaN(date.getTime())) return 'Recent';

  const today = new Date();
  const startOf = d => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOf(today) - startOf(date)) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return date.toLocaleDateString(undefined, { weekday: 'long' });
  return formatDateWithFmt(value, fmt);
}

const TYPE_ICON = {
  observation: 'binoculars',
  measurement: 'ruler',
  photo: 'camera',
  plant_edit: 'pencil',
};

const TYPE_LABEL = {
  observation: 'Observation',
  measurement: 'Measurement',
  photo: 'Photo',
  plant_edit: 'Plant edit',
};

const CLUSTER_PREVIEW_COUNT = 3;

function ActivityItem({ item, clusterPosition = 'single' }) {
  const isPhoto = item.type === 'photo' && item.photo_id;
  const imageId = isPhoto ? item.photo_id : item.cover_photo_id;
  const imageSrc = isPhoto
    ? photoUrl(item.photo_id, item.photo_rotation_version, item.photo_created_at)
    : photoUrl(item.cover_photo_id, item.cover_photo_rotation_version, item.cover_photo_created_at);
  const icon = TYPE_ICON[item.type] || 'activity';
  const actorLabel = item.actor?.display_name || item.actor?.email;
  const { formatDate } = useDateFormat();

  return (
    <Link to={`/plants/${item.plant_id}`} state={{ returnTo: '/activity' }} className="activity-item-link">
      <article className={`activity-item activity-item--${item.type} activity-item--${clusterPosition}`}>
        <div className="activity-media">
          {imageId ? (
            <img
              src={imageSrc}
              alt={item.plant_display_name}
              className="activity-photo"
              loading="lazy"
            />
          ) : (
            <div className="activity-photo activity-photo--empty">
              <Icon name="sprout" size={22} />
            </div>
          )}
          <span className="activity-type-icon">
            <Icon name={icon} size={13} />
          </span>
        </div>

        <div className="activity-body">
          <div className="activity-row">
            <span className={item.event_type ? `event-badge event-${item.event_type}` : 'activity-type-label'}>
              {item.event_type ? item.title : TYPE_LABEL[item.type] || item.title}
            </span>
            <time className="activity-date">{formatDate(item.activity_date)}</time>
          </div>

          <h2 className="activity-title">{item.plant_display_name}</h2>
          <p className="activity-meta">
            <span className="specimen-id">{item.specimen_id}</span>
            {item.plant_scientific_name && (
              <span><em>{item.plant_scientific_name}</em></span>
            )}
          </p>

          {item.summary && <p className="activity-summary">{item.summary}</p>}
          {item.notes && <p className="activity-notes">{item.notes}</p>}
          {item.created_at && (
            <p className="activity-logged-meta">
              Logged {formatLoggedTime(item.created_at)}
              {actorLabel ? ` by ${actorLabel}` : ''}
            </p>
          )}
        </div>

        <Icon name="chevronRight" size={16} className="activity-chevron" />
      </article>
    </Link>
  );
}

function ActivityCluster({ items, expanded, onToggle }) {
  const clustered = items.length > 1;
  const plant = items[0];
  const canCollapse = clustered && items.length > CLUSTER_PREVIEW_COUNT;
  const visibleItems = canCollapse && !expanded ? items.slice(0, CLUSTER_PREVIEW_COUNT) : items;
  const hiddenCount = items.length - visibleItems.length;

  return (
    <div className={clustered ? 'activity-cluster activity-cluster--stacked' : 'activity-cluster'}>
      {clustered && (
        <button
          type="button"
          className={`activity-cluster-header ${canCollapse ? 'activity-cluster-header--toggle' : ''}`}
          onClick={canCollapse ? onToggle : undefined}
          aria-expanded={canCollapse ? expanded : undefined}
        >
          <div>
            <span className="activity-cluster-name">{plant.plant_display_name}</span>
            <span className="activity-cluster-id">{plant.specimen_id}</span>
          </div>
          <span className="activity-cluster-count">
            {items.length} updates
            {canCollapse && <Icon name={expanded ? 'chevronUp' : 'chevronDown'} size={13} />}
          </span>
        </button>
      )}
      {visibleItems.map((item, index) => (
        <ActivityItem
          key={`${item.type}-${item.id}`}
          item={item}
          clusterPosition={
            !clustered ? 'single' :
            index === 0 ? 'first' :
            index === visibleItems.length - 1 ? 'last' :
            'middle'
          }
        />
      ))}
      {hiddenCount > 0 && (
        <button type="button" className="activity-cluster-more" onClick={onToggle}>
          Show {hiddenCount} more
        </button>
      )}
    </div>
  );
}

export default function Activity() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState('logged_desc');
  const [expandedClusters, setExpandedClusters] = useState(() => new Set());
  const { dateFmt } = useDateFormat();

  const fetchActivity = useCallback(() => {
    setLoading(true);
    setError(null);
    return fetch(`/api/activity?sort=${encodeURIComponent(sort)}&limit=100`)
      .then(r => {
        if (!r.ok) throw new Error('Failed to load recent activity');
        return r.json();
      })
      .then(data => {
        setItems(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [sort]);

  useEffect(() => { fetchActivity(); }, [fetchActivity]);

  const refreshing = usePullToRefresh(fetchActivity);

  const toggleCluster = (key) => {
    setExpandedClusters(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const grouped = useMemo(() => {
    const groups = [];
    for (const item of items) {
      const label = formatGroupDate(item.activity_date, dateFmt);
      const existing = groups[groups.length - 1];
      if (existing?.label === label) {
        existing.items.push(item);
      } else {
        groups.push({ key: `${label}-${groups.length}-${item.type}-${item.id}`, label, items: [item] });
      }
    }
    return groups.map(group => {
      const clusters = [];
      for (const item of group.items) {
        const existing = clusters[clusters.length - 1];
        if (existing?.plantId === item.plant_id) {
          existing.items.push(item);
        } else {
          clusters.push({ plantId: item.plant_id, key: `${group.key}-${clusters.length}-${item.plant_id}-${item.type}-${item.id}`, items: [item] });
        }
      }
      return { ...group, clusters };
    });
  }, [items, dateFmt]);

  if (loading) return <SkeletonActivityFeed />;
  if (error) return (
    <div className="app activity-screen">
      <header className="app-header">
        <div className="app-header-brand">
          <img src="/logo_only.svg" alt="Aroidex" className="app-header-logo" />
          <div><h1>Recent Activity</h1></div>
        </div>
      </header>
      <ErrorScreen message={error} onRetry={fetchActivity} />
    </div>
  );

  return (
    <div className="app activity-screen">
      {refreshing && (
        <div className="plan-ptr-indicator">
          <Icon name="rotateCw" size={16} /> Refreshing…
        </div>
      )}
      <header className="app-header">
        <div className="app-header-brand">
          <img src="/logo_only.svg" alt="Aroidex" className="app-header-logo" />
          <div>
            <h1>Recent Activity</h1>
            <p className="plant-count">{items.length} latest update{items.length === 1 ? '' : 's'}</p>
          </div>
        </div>
      </header>

      <div className="activity-sort-bar">
        <span className="activity-sort-label">Sort by</span>
        <div className="activity-sort-chips">
          <button
            type="button"
            className={`activity-sort-chip ${sort.startsWith('logged') ? 'activity-sort-chip--active' : ''}`}
            onClick={() => setSort(sort === 'logged_desc' ? 'logged_asc' : 'logged_desc')}
          >
            Logged {sort === 'logged_asc' ? '↑' : '↓'}
          </button>
          <button
            type="button"
            className={`activity-sort-chip ${sort.startsWith('activity') ? 'activity-sort-chip--active' : ''}`}
            onClick={() => setSort(sort === 'activity_desc' ? 'activity_asc' : 'activity_desc')}
          >
            Activity date {sort === 'activity_asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="activity-empty">
          <Icon name="activity" size={28} />
          <p>No activity yet.</p>
        </div>
      ) : (
        <div className="activity-feed">
          {grouped.map(group => (
            <section key={group.key} className="activity-group">
              <h2 className="activity-group-title">{group.label}</h2>
              <div className="activity-group-list">
                {group.clusters.map(cluster => (
                  <ActivityCluster
                    key={cluster.key}
                    items={cluster.items}
                    expanded={expandedClusters.has(cluster.key)}
                    onToggle={() => toggleCluster(cluster.key)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
